import { useState } from "react"

import useHasAccess from "@hooks/useHasAccess"

import { findBrandSubscription, findCatalogSubscription, findCategorySubscription } from "@utils/catalogSubscriptions"
import { faIcon } from "@utils/faIcon"

import { Button } from "@atoms/index"

import AlertBadge from "@molecules/AlertBadge/AlertBadge"
import { Prompt } from "@molecules/index"

import useTable from "@organisms/Table/Table.context"

import {
    useCatalogSubscriptionsQuery,
    useSubscribeMutation,
    useSubscriptionPreviewCountMutation,
    useUnsubscribeMutation,
} from "@pages/Settings/SettingsCatalogs/SettingsCatalogs.store"
import { SubscribePayload } from "@pages/Settings/SettingsCatalogs/SettingsCatalogs.types"
import {
    getCategoryTabId,
    getCategoryTitle,
} from "@pages/Settings/SettingsCatalogs/SettingsCatalogsItemList/SettingsCatalogsItemList.utils"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import { ScopeSubscriptionCTAProps } from "./ScopeSubscriptionCTA.types"

export default function ScopeSubscriptionCTA(props: ScopeSubscriptionCTAProps) {
    const { catalogId, brandId, brandName, categories } = props

    const { hasPermission } = useHasAccess()
    const hasPricebookEditPermission = hasPermission("pricebook_edit_permission", PERMISSION_LEVEL.FULL)

    const { activeTab } = useTable()
    const subscriptionsQuery = useCatalogSubscriptionsQuery(catalogId)
    const previewCountMutation = useSubscriptionPreviewCountMutation()
    const subscribeMutation = useSubscribeMutation()
    const unsubscribeMutation = useUnsubscribeMutation()

    const [isSubscribePromptOpen, setIsSubscribePromptOpen] = useState(false)
    const [isUnsubscribePromptOpen, setIsUnsubscribePromptOpen] = useState(false)

    const activeCategory = categories.find((category) => activeTab?.id === getCategoryTabId(category))

    // Inside a category path the brandless bucket is labelled like a brand; on its own it reads as a phrase.
    const brandLabel = brandId === null ? "No brand" : brandName
    const brandScopeLabel = brandId === null ? "items with no brand" : brandName
    const scopeLabel = activeCategory ? `${brandLabel} / ${getCategoryTitle(activeCategory)}` : brandScopeLabel
    const scopeNoun = activeCategory ? "category" : "brand"
    const brandItemsScope = brandId === null ? "without a brand" : "to this brand"
    const newItemsScope = activeCategory ? "to this category" : brandItemsScope

    // The active scope is subscribed when its own row exists, or when a broader one already covers
    // it: the whole catalog, or the brand when a category tab is active.
    const subscriptions = subscriptionsQuery.data ?? []
    const brandSubscription = findBrandSubscription(subscriptions, brandId)
    const exactSubscription = activeCategory
        ? findCategorySubscription(subscriptions, brandId, activeCategory.id)
        : brandSubscription
    const broaderSubscription = findCatalogSubscription(subscriptions) ?? (activeCategory ? brandSubscription : null)
    const coveringSubscription = exactSubscription ?? broaderSubscription

    const subscribePayload: SubscribePayload = {
        catalog: catalogId,
        level: activeCategory ? "category" : "brand",
        brand: brandId ?? undefined,
        category: activeCategory?.id,
    }

    const handleSubscribeClick = () => {
        setIsSubscribePromptOpen(true)
        previewCountMutation.mutate(subscribePayload)
    }

    // The subscribe/unsubscribe mutations invalidate the shared subscriptions cache themselves, so the
    // badge and the item rows both re-derive without this page refetching anything.
    const handleSubscribeConfirm = () => {
        setIsSubscribePromptOpen(false)
        subscribeMutation.mutate(subscribePayload)
    }

    const handleUnsubscribeConfirm = () => {
        setIsUnsubscribePromptOpen(false)
        if (exactSubscription) {
            unsubscribeMutation.mutate(exactSubscription)
        }
    }

    const previewCount = previewCountMutation.data?.count
    const importedItems =
        previewCount === undefined ? "the covered items" : `${previewCount} ${previewCount === 1 ? "item" : "items"}`
    const subscribePromptBody = `This will import ${importedItems} into your pricebook and keep them in sync. New items added ${newItemsScope} later will be imported automatically.`
    const unsubscribePromptBody = `Items already in your pricebook will stay there but will no longer receive catalog updates. New items added ${newItemsScope} will no longer be imported automatically.`

    if (coveringSubscription) {
        return (
            <>
                <AlertBadge status="success" size="md" showIcon={true} isLoading={false}>
                    {exactSubscription ? "Subscribed" : `Subscribed via ${coveringSubscription.level}`}
                </AlertBadge>
                {hasPricebookEditPermission && exactSubscription && (
                    <>
                        <Button
                            variant="subtle"
                            size="sm"
                            onClick={() => setIsUnsubscribePromptOpen(true)}
                            isProcessing={unsubscribeMutation.isPending}
                            isDisabled={unsubscribeMutation.isPending}
                        >
                            Unsubscribe
                        </Button>

                        <Prompt
                            title={`Unsubscribe from ${scopeLabel}?`}
                            body={unsubscribePromptBody}
                            isOpen={isUnsubscribePromptOpen}
                            confirmText="Unsubscribe"
                            onConfirm={handleUnsubscribeConfirm}
                            cancelText="Cancel"
                            onCancel={() => setIsUnsubscribePromptOpen(false)}
                        />
                    </>
                )}
            </>
        )
    }

    if (!hasPricebookEditPermission) {
        return null
    }

    return (
        <>
            <Button
                variant="solid"
                size="sm"
                leftIcon={faIcon({ name: "circle-plus", style: "regular" })}
                onClick={handleSubscribeClick}
                isProcessing={subscribeMutation.isPending}
                isDisabled={subscribeMutation.isPending}
            >
                Subscribe to {scopeNoun}
            </Button>

            <Prompt
                title={`Subscribe to ${scopeLabel}?`}
                body={subscribePromptBody}
                isOpen={isSubscribePromptOpen}
                isLoading={previewCountMutation.isPending}
                confirmText="Subscribe"
                onConfirm={handleSubscribeConfirm}
                cancelText="Cancel"
                onCancel={() => setIsSubscribePromptOpen(false)}
            />
        </>
    )
}
